import { useState } from 'react';
import { Trash2, AlertTriangle, CheckCircle2, XCircle, Loader2 } from 'lucide-react';
import { toast } from 'react-hot-toast';

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { trpc } from '@/lib/api/trpc';

interface CleanupOrphansDialogProps {
  id: string;
}

export function CleanupOrphansDialog({ id }: CleanupOrphansDialogProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [previewResult, setPreviewResult] = useState<any>(null);
  const utils = trpc.useUtils();

  const previewMutation = trpc.pluginUpdates.cleanupOrphanRoutes.useMutation({
    onSuccess: (result) => {
      setPreviewResult(result);
      
      if (result.orphansFound === 0) {
        toast.success('No orphan routes found! 🎉');
      } else {
        toast.success(`Found ${result.orphansFound} orphan route(s) to clean`);
      }
    },
    onError: (error) => {
      toast.error(`Preview failed: ${error.message}`);
    },
  });

  const cleanupMutation = trpc.pluginUpdates.cleanupOrphanRoutes.useMutation({
    onSuccess: (result) => {
      toast.success(
        `✅ Cleaned ${result.orphansRemoved} orphan route(s)`,
        { duration: 5000 }
      );
      
      // Invalidate mount points to refresh the list
      utils.plugins.listMountPoints.invalidate({ storeId: id });
      
      // Close dialog
      setIsOpen(false);
      setPreviewResult(null);
    },
    onError: (error) => {
      toast.error(`Cleanup failed: ${error.message}`);
    },
  });

  const handlePreview = () => {
    setPreviewResult(null);
    previewMutation.mutate({
      storeId: id,
      dryRun: true,
    });
  };

  const handleCleanup = () => {
    if (!previewResult) return;

    cleanupMutation.mutate({
      storeId: id,
      dryRun: false,
    });
  };

  const handleOpenChange = (open: boolean) => {
    setIsOpen(open);
    if (!open) {
      setPreviewResult(null);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        <Button variant="outline" size="default">
          <Trash2 className="mr-2 h-4 w-4" />
          Clean Orphans
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Trash2 className="h-5 w-5" />
            Cleanup Orphan Routes
          </DialogTitle>
          <DialogDescription>
            Remove routes pointing to deleted deployments. This helps keep your routing table clean and
            performant.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Preview section */}
          {!previewResult && (
            <div className="rounded-lg border border-orange-200 bg-orange-50 p-4">
              <div className="flex items-start gap-3">
                <AlertTriangle className="mt-0.5 h-5 w-5 flex-shrink-0 text-orange-600" />
                <div className="flex-1">
                  <h4 className="font-medium text-orange-900">Preview First</h4>
                  <p className="mt-1 text-sm text-orange-700">
                    Click &quot;Preview Cleanup&quot; to see which routes would be removed. No changes will be
                    made until you confirm.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Preview results */}
          {previewResult && (
            <div className="space-y-3">
              {/* Summary */}
              <div className="rounded-lg border bg-card p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium">Orphan Routes Found</p>
                    <p className="text-2xl font-bold">{previewResult.orphansFound}</p>
                  </div>
                  {previewResult.orphansFound === 0 ? (
                    <CheckCircle2 className="h-8 w-8 text-green-500" />
                  ) : (
                    <AlertTriangle className="h-8 w-8 text-orange-500" />
                  )}
                </div>
              </div>

              {/* List of orphan routes */}
              {previewResult.orphansFound > 0 && (
                <div className="max-h-96 space-y-2 overflow-y-auto rounded-lg border bg-card p-4">
                  <h4 className="mb-3 font-medium">Routes to be Removed:</h4>
                  {previewResult.routes.map((route: any, idx: number) => (
                    <div
                      key={route.routeId}
                      className="rounded-md border border-red-200 bg-red-50 p-3"
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <XCircle className="h-4 w-4 text-red-600" />
                            <code className="text-sm font-mono">
                              {route.hostname}
                              {route.basePath}
                            </code>
                          </div>
                          <div className="mt-2 space-y-1 text-xs text-red-700">
                            <p>
                              <span className="font-medium">Instance:</span>{' '}
                              <code>{route.instanceId}</code>
                            </p>
                            <p>
                              <span className="font-medium">Deployment:</span>{' '}
                              <code>{route.deploymentId}</code>
                            </p>
                            <p>
                              <span className="font-medium">Reason:</span>{' '}
                              {route.reason === 'deployment_not_found'
                                ? 'Deployment not found in database'
                                : 'Instance not found'}
                            </p>
                          </div>
                        </div>
                        <Badge variant="destructive" className="text-xs">
                          Orphan
                        </Badge>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Errors (if any) */}
              {previewResult.errors && previewResult.errors.length > 0 && (
                <div className="rounded-lg border border-red-200 bg-red-50 p-4">
                  <h4 className="mb-2 font-medium text-red-900">Errors:</h4>
                  <ul className="space-y-1 text-sm text-red-700">
                    {previewResult.errors.map((error: string, idx: number) => (
                      <li key={idx}>• {error}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>

        <DialogFooter>
          {!previewResult ? (
            <>
              <Button variant="outline" onClick={() => setIsOpen(false)}>
                Cancel
              </Button>
              <Button onClick={handlePreview} disabled={previewMutation.isPending}>
                {previewMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Preview Cleanup
              </Button>
            </>
          ) : (
            <>
              <Button variant="outline" onClick={() => setPreviewResult(null)}>
                Back
              </Button>
              <Button
                variant="destructive"
                onClick={handleCleanup}
                disabled={
                  cleanupMutation.isPending || previewResult.orphansFound === 0
                }
              >
                {cleanupMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                {previewResult.orphansFound === 0
                  ? 'No Cleanup Needed'
                  : `Remove ${previewResult.orphansFound} Route(s)`}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

